import { jsxs as e, jsx as s } from "react/jsx-runtime";
import { withManagedProps as p } from "@made-people/managed-props";
import { z as a } from "zod";
import { withZod as g } from "@made-people/react-prop-guard";
import { blogPostBlueprint as d } from "./index-blueprint.es.js";
import { Markdown as m } from "@made-people/blueprint-markdown";
import { useTranslate as y } from "@made-people/grease-translations";
import { componentWrapperSchema as k, ComponentWrapper as b } from "@made-people/blueprint-component-wrapper";
import "@made-people/blueprint";
const w = a.object({
  variants: a.custom().optional(),
  layout: k.shape.layout.optional(),
  content: a.string().optional(),
  author: a.string().optional(),
  latestUpdate: a.string().optional(),
  published: a.string().optional()
}), H = p(
  "BlogPostMarkdown",
  g(
    w,
    ({ variants: c, content: l, author: r, latestUpdate: n, published: t, layout: h }) => {
      const o = d.main.styles(c), u = r || t || n, f = t || n, i = y();
      return /* @__PURE__ */ e(b, { className: o.wrapper, layout: { ...h }, children: [
        u && /* @__PURE__ */ e("header", { className: o.headerWrapper, children: [
          r && /* @__PURE__ */ e("h3", { className: o.author, children: [
            i("Blog_Post_Author_Label"),
            ": ",
            /* @__PURE__ */ s("span", { children: r })
          ] }),
          f && /* @__PURE__ */ e("p", { className: o.published, children: [
            t && /* @__PURE__ */ e("span", { children: [
              i("Blog_Post_Published"),
              ":",
              " ",
              /* @__PURE__ */ s("span", { children: t })
            ] }),
            " ",
            t && n && "|",
            n && /* @__PURE__ */ e("span", { children: [
              i("Blog_Post_Last_Updated"),
              ":",
              " ",
              /* @__PURE__ */ s("span", { children: n })
            ] })
          ] })
        ] }),
        l && /* @__PURE__ */ s(
          m,
          {
            content: l,
            blueprint: d.markdown
          }
        )
      ] });
    }
  )
), j = `
    <h1>The Evolution and Impact of Sneaker Culture</h1>
    <p>
      <em>
        Sneakers have evolved from mere athletic wear to cultural icons,
        influencing fashion, music, and lifestyle across the globe. This
        transformation has not only changed the way we view footwear but has
        also led to innovations in design, technology, and marketing strategies.
      </em>
    </p>
    <img
      src="https://images.unsplash.com/photo-1633303422361-5c47486b2a6b?q=80&w=1892&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      alt="Tux, the Linux mascot"
    />
    <h3>Content</h3>
    <ol>
      <li>
        <a href="#popular-sneaker-models-and-their-features">
          Popular Sneaker Models and Their Features
        </a>
      </li>
      <li>
        <a href="#how-to-style-sneakers-for-different-occasions">
          How to Style Sneakers for Different Occasions
        </a>
      </li>
      <li>
        <a href="#membership">Membership</a>
      </li>
      <li>
        <a href="#technological-advancements">Technological Advancements</a>
      </li>
      <li>
        <a href="#sneaker-releases-and-hype">Sneaker Releases and Hype</a>
      </li>
      <li>
        <a href="#collecting-and-reselling">Collecting and Reselling</a>
      </li>
      <li>
        <a href="#sneaker-care-and-maintenance">Sneaker Care and Maintenance</a>
      </li>
    </ol>
    <h2>Introduction to Sneaker Culture</h2>
    <p>
      Sneaker culture has its roots in the 1980s, with the rise of hip hop and
      basketball. Icons like Michael Jordan and Run DMC made sneakers symbols of
      status, style, and identity. Today, sneakers are a universal language of
      creativity and self-expression.
    </p>
    <h2>Design Innovations</h2>
    <p>
      The design of sneakers has seen a radical shift, with brands collaborating
      with artists, designers, and celebrities to create unique, often
      limited-edition models. Sustainability has also become a key focus,
      leading to the use of eco-friendly materials and processes.
    </p>
    <h3>Key Collaborations</h3>
    <ul>
      <li>
        <strong>Adidas and Kanye West</strong>: The Yeezy line has become
        synonymous with cutting-edge design and exclusivity.
      </li>
      <li>
        <strong>Nike and Virgil Abloh</strong>: The Off-White™ collaborations
        deconstruct classic models to create something entirely new.
      </li>
    </ul>
    <h2 id="popular-sneaker-models-and-their-features">
      Popular Sneaker Models and Their Features
    </h2>
    <p>
      Below is a table showcasing some of the most iconic sneaker models, along
      with their distinctive features and the year they were released.
    </p>
    <table>
      <thead>
        <tr>
          <th>Sneaker Model</th>
          <th>Features</th>
          <th>Release Year</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Air Jordan 1</td>
          <td>High-top design, Air Sole unit</td>
          <td>1985</td>
        </tr>
        <tr>
          <td>Adidas Yeezy Boost 350</td>
          <td>Primeknit upper, Boost cushioning</td>
          <td>2015</td>
        </tr>
        <tr>
          <td>Nike Air Max 1</td>
          <td>Visible Air cushioning unit, suede and mesh upper</td>
          <td>1987</td>
        </tr>
        <tr>
          <td>Converse Chuck Taylor All Star</td>
          <td>Canvas upper, rubber sole</td>
          <td>1917</td>
        </tr>
        <tr>
          <td>Puma Suede</td>
          <td>Suede upper, narrow silhouette</td>
          <td>1968</td>
        </tr>
      </tbody>
    </table>
    <h2 id="how-to-style-sneakers-for-different-occasions">
      How to Style Sneakers for Different Occasions
    </h2>
    <p>
      Sneakers have transcended their athletic origins to become versatile
      wardrobe staples. Here’s how you can style sneakers for various occasions:
    </p>
    <h3>Casual Outings</h3>
    <p>
      Pair classic low-top sneakers with jeans and a t-shirt for a timeless,
      casual look. Add a leather jacket or a casual blazer to elevate the
      outfit.
    </p>
    <blockquote>
      <h4>
        "Sneakers tell stories of culture, innovation, and the relentless
        pursuit of personal expression. They're not just part of the fashion
        industry; they're woven into the fabric of our lives."
      </h4>
      <p>— Tinker Hatfield, legendary sneaker designer</p>
    </blockquote>
    <h3>Work</h3>
    <p>
      Many workplaces now embrace smart casual attire. Combine minimalist
      sneakers with chinos and a button-down shirt for a comfortable yet
      professional look.
    </p>
    <h3>Evening Events</h3>
    <p>
      For a more daring fashion statement, pair bold or high-top sneakers with a
      suit or a chic dress. Choose sleek designs and darker colors for a
      cohesive look.
    </p>
    <h3>Active Wear</h3>
    <p>
      Technical sneakers are best for active wear. Pair them with leggings or
      shorts and a breathable top for a functional yet stylish gym outfit.
    </p>
    <h3 id="membership">Membership</h3>
    <ol>
      <li>
        <strong>How to Join</strong>: Simply create an account on sneakers.com
        to become a part of the "People Gang." Registration is straightforward
        and opens the door to numerous benefits.
      </li>
      <li>
        <strong>Earning Points</strong>: Members earn points for every purchase
        they make. These points can be accumulated and later used for discounts
        on future orders.
      </li>
      <li>
        <strong>Additional Benefits</strong>:
        <ul>
          <li>
            <strong>Exclusive Offers and Promotions</strong>: Members get access
            to special offers, promotions, and discounts that are not available
            to regular customers.
          </li>
          <li>
            <strong>Birthday Bonuses</strong>: Enjoy special treats and offers
            on your birthday as a member of the People Gang.
          </li>
          <li>
            <strong>Early Access to New Products</strong>: Get early access to
            new flavors and products before they are released to the general
            public.
          </li>
        </ul>
      </li>
      <li>
        <strong>Tiered Membership Levels</strong>: The People Gang features
        different tiers based on your engagement and purchasing frequency, with
        each tier offering its unique set of rewards and benefits.
      </li>
    </ol>
    <h2 id="technological-advancements">Technological Advancements</h2>
    <p>
      Technology has revolutionized sneaker manufacturing, with advancements in
      cushioning, durability, and performance. Nike's Air technology and Adidas'
      Boost are prime examples of how sneakers have become more comfortable and
      efficient.
    </p>
    <h2 id="sneaker-releases-and-hype">Sneaker Releases and Hype</h2>
    <p>
      The marketing of sneaker releases has become an art form, with brands
      leveraging scarcity and hype to fuel demand. Social media and sneaker apps
      play a crucial role in announcing drops, creating a frenzy among
      collectors and enthusiasts.
    </p>
    <h2 id="collecting-and-reselling">Collecting and Reselling</h2>
    <p>
      Sneaker collecting has grown into a lucrative market, with rare pairs
      selling for thousands of dollars. Platforms like StockX and GOAT
      facilitate the buying and selling, making it easier for collectors to find
      coveted models.
    </p>
    <h3>Tips for Collectors</h3>
    <ul>
      <li>
        <strong>Stay Informed</strong>: Follow sneaker news and join online
        communities to get the latest on releases and restocks.
      </li>
      <li>
        <strong>Authenticate</strong>: Ensure authenticity by purchasing from
        reputable sources.
      </li>
    </ul>
    <h2 id="sneaker-care-and-maintenance">Sneaker Care and Maintenance</h2>
    <p>
      Maintaining sneakers is essential to preserving their appearance and
      value. Regular cleaning, proper storage, and minor repairs can extend
      their lifespan significantly.
    </p>
    <h2>Future Trends</h2>
    <p>
      The sneaker industry continues to evolve, with sustainability, digital
      integration (like AR try-ons), and customization being the forefront of
      future trends. As technology and fashion merge, the possibilities for what
      sneakers can represent are limitless.
    </p>
    <p>
      Sneakers are more than just footwear; they are a reflection of culture,
      innovation, and personal identity. As we look to the future, the sneaker
      industry promises to continue its trajectory of growth and influence in
      new and exciting ways.
    </p>
`, R = `
# The Evolution and Impact of Sneaker Culture

_Sneakers have evolved from mere athletic wear to cultural icons, influencing fashion, music, and lifestyle across the globe. This transformation has not only changed the way we view footwear but has also led to innovations in design, technology, and marketing strategies._

![Tux, the Linux mascot](https://images.unsplash.com/photo-1633303422361-5c47486b2a6b?q=80&w=1892&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)

### Content
1. [Popular Sneaker Models and Their Features](#popular-sneaker-models-and-their-features)
2. [How to Style Sneakers for Different Occasions](#how-to-style-sneakers-for-different-occasions)
3. [Membership](#membership)
4. [Technological Advancements](#technological-advancements)
5. [Sneaker Releases and Hype](#sneaker-releases-and-hype)
6. [Collecting and Reselling](#collecting-and-reselling)
7. [Sneaker Care and Maintenance](#sneaker-care-and-maintenance)


## Introduction to Sneaker Culture

Sneaker culture has its roots in the 1980s, with the rise of hip hop and basketball. Icons like Michael Jordan and Run DMC made sneakers symbols of status, style, and identity. Today, sneakers are a universal language of creativity and self-expression.

## Design Innovations

The design of sneakers has seen a radical shift, with brands collaborating with artists, designers, and celebrities to create unique, often limited-edition models. Sustainability has also become a key focus, leading to the use of eco-friendly materials and processes.

### Key Collaborations

- **Adidas and Kanye West**: The Yeezy line has become synonymous with cutting-edge design and exclusivity.
- **Nike and Virgil Abloh**: The Off-White™ collaborations deconstruct classic models to create something entirely new.


## Popular Sneaker Models and Their Features

Below is a table showcasing some of the most iconic sneaker models, along with their distinctive features and the year they were released.

| Sneaker Model       | Features                                           | Release Year |
|---------------------|----------------------------------------------------|--------------|
| Air Jordan 1        | High-top design, Air Sole unit                     | 1985         |
| Adidas Yeezy Boost 350 | Primeknit upper, Boost cushioning                | 2015         |
| Nike Air Max 1      | Visible Air cushioning unit, suede and mesh upper  | 1987         |
| Converse Chuck Taylor All Star | Canvas upper, rubber sole               | 1917         |
| Puma Suede          | Suede upper, narrow silhouette                     | 1968         |



## How to Style Sneakers for Different Occasions

Sneakers have transcended their athletic origins to become versatile wardrobe staples. Here’s how you can style sneakers for various occasions:

### Casual Outings

Pair classic low-top sneakers with jeans and a t-shirt for a timeless, casual look. Add a leather jacket or a casual blazer to elevate the outfit.

> ### "Sneakers tell stories of culture, innovation, and the relentless pursuit of personal expression. They're not just part of the fashion industry; they're woven into the fabric of our lives."
> ##### - Tinker Hatfield, legendary sneaker designer

### Work

Many workplaces now embrace smart casual attire. Combine minimalist sneakers with chinos and a button-down shirt for a comfortable yet professional look.

### Evening Events

For a more daring fashion statement, pair bold or high-top sneakers with a suit or a chic dress. Choose sleek designs and darker colors for a cohesive look.

### Active Wear

Technical sneakers are best for active wear. Pair them with leggings or shorts and a breathable top for a functional yet stylish gym outfit.


### Membership

1. **How to Join**: Simply create an account on sneakers.com to become a part of the "People Gang." Registration is straightforward and opens the door to numerous benefits.

2. **Earning Points**: Members earn points for every purchase they make. These points can be accumulated and later used for discounts on future orders.

3. **Additional Benefits**:
   - **Exclusive Offers and Promotions**: Members get access to special offers, promotions, and discounts that are not available to regular customers.
   - **Birthday Bonuses**: Enjoy special treats and offers on your birthday as a member of the People Gang.
   - **Early Access to New Products**: Get early access to new flavors and products before they are released to the general public.

4. **Tiered Membership Levels**: The People Gang features different tiers based on your engagement and purchasing frequency, with each tier offering its unique set of rewards and benefits.

## Technological Advancements

Technology has revolutionized sneaker manufacturing, with advancements in cushioning, durability, and performance. Nike's Air technology and Adidas' Boost are prime examples of how sneakers have become more comfortable and efficient.

## Sneaker Releases and Hype

The marketing of sneaker releases has become an art form, with brands leveraging scarcity and hype to fuel demand. Social media and sneaker apps play a crucial role in announcing drops, creating a frenzy among collectors and enthusiasts.

## Collecting and Reselling

Sneaker collecting has grown into a lucrative market, with rare pairs selling for thousands of dollars. Platforms like StockX and GOAT facilitate the buying and selling, making it easier for collectors to find coveted models.

### Tips for Collectors

- **Stay Informed**: Follow sneaker news and join online communities to get the latest on releases and restocks.
- **Authenticate**: Ensure authenticity by purchasing from reputable sources.

## Sneaker Care and Maintenance

Maintaining sneakers is essential to preserving their appearance and value. Regular cleaning, proper storage, and minor repairs can extend their lifespan significantly.

## Future Trends

The sneaker industry continues to evolve, with sustainability, digital integration (like AR try-ons), and customization being the forefront of future trends. As technology and fashion merge, the possibilities for what sneakers can represent are limitless.

Sneakers are more than just footwear; they are a reflection of culture, innovation, and personal identity. As we look to the future, the sneaker industry promises to continue its trajectory of growth and influence in new and exciting ways.

`;
export {
  H as BlogPostMarkdown,
  j as MOCK_HTML,
  R as MOCK_MARKDOWN,
  w as blogPostMarkdownSchema
};
//# sourceMappingURL=index.es.js.map
